/**
 * Computers.
 *
 * The machines that sync into this account, each with an area of its own —
 * separate from My Files, and labelled by the machine it came from.
 *
 * ── Why the names are decrypted here and not on the server ──────────────────
 * A computer's root is an ordinary node: its name is `name_enc`, wrapped under
 * the account's master key like the vault root's. The server holds a `dev_` id
 * and a plaintext *label* — the one the approval screen shows next to a
 * fingerprint, which has to be readable at that moment — and cannot read the
 * folder name at all. So this page has two names for a machine and shows the
 * label, which is the one a person set.
 *
 * ── A registered device is not yet a computer ───────────────────────────────
 * `vault_devices` gains a row when an app registers a key and a human approves
 * it. That is the credential, and it happens before anything syncs. The root
 * node arrives later, the first time the machine actually has somewhere to put
 * things — so `rootNodeId` is null for a while, and the page says "not syncing
 * yet" rather than drawing an empty folder somebody would click into.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, errorText, type ComputerWire } from '../api';
import { ds } from '../ds';
import { DataRow, DataTable, Eyebrow, PageTitle, Panel, type Column } from '../components/Shell';
import { SkeletonRows } from '../components/Skeleton';
import { unwrapNodeKey } from '../crypto/keys';
import { decryptName } from '../crypto/metadata';
import { requireVault } from '../crypto/vault';
import { formatBytes, formatWhen, monoPlain } from '../theme';
import { useRouter } from '../router';
import { appName, isAppArea } from '../lib/appscope';

const COLUMNS: Column[] = [
  { width: 38 },
  { label: 'Computer', width: 'flex' },
  // Last seen stays longest: it is the one that says whether a machine is
  // still syncing.
  { label: 'Files', width: 80, align: 'right', hideBelow: 660 },
  { label: 'Size', width: 100, align: 'right', hideBelow: 560 },
  { label: 'Last seen', width: 130, align: 'right' }
];

/** The same table for the apps' areas, headed for what they are. */
const APP_COLUMNS: Column[] = COLUMNS.map((c) => (c.label === 'Computer' ? { ...c, label: 'App' } : c));

interface Computer extends ComputerWire {
  /** The folder's own name, decrypted, or null when there is no root yet. */
  folderName: string | null;
}

export function Computers() {
  const { Icon } = ds();
  const { navigate } = useRouter();

  const [computers, setComputers] = useState<Computer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { computers: wire } = await api.computers.list();
      setComputers(await Promise.all(wire.map(withFolderName)));
    } catch (err) {
      setError(errorText(err, 'Could not load your computers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Every linked device can have an area; only the hub's is a computer's.
  const machines = computers.filter((c) => !isAppArea(c.client));
  const apps = computers.filter((c) => isAppArea(c.client) && c.rootNodeId);
  const syncing = machines.filter((c) => c.rootNodeId);

  const rows = (list: Computer[], app: boolean) =>
    list.map((c, i) => (
      <DataRow
        key={c.id}
        columns={app ? APP_COLUMNS : COLUMNS}
        first={i === 0}
        tile={
          <span style={{ display: 'flex', opacity: c.rootNodeId ? 1 : 0.5 }}>
            <Icon name={app ? 'layout-grid' : 'monitor'} size={17} />
          </span>
        }
        // Only a computer with a root has anywhere to go.
        onOpen={c.rootNodeId ? () => navigate(`/files/${c.rootNodeId}`) : undefined}
        cells={[
          <span key="name" style={{ display: 'grid', gap: 2 }}>
            <span style={{ fontWeight: 500 }}>
              {app ? appName(c.client!) : (c.label ?? 'Unnamed computer')}
            </span>
            <span style={{ ...monoPlain, fontSize: 11.5 }}>
              {c.rootNodeId
                ? (c.folderName ?? 'This folder could not be read')
                : 'Not syncing yet'}
            </span>
          </span>,
          <span key="files">{c.rootNodeId ? c.files : '—'}</span>,
          <span key="size">{c.rootNodeId ? formatBytes(c.bytes) : '—'}</span>,
          <span key="seen">{c.lastUsedAt ? formatWhen(c.lastUsedAt) : '—'}</span>
        ]}
      />
    ));

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <PageTitle
        title="Computers"
        meta={
          machines.length === 0
            ? undefined
            : `${syncing.length} of ${machines.length} syncing · ` +
              formatBytes(machines.reduce((n, c) => n + c.bytes, 0))
        }
      />

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {loading ? (
        <Panel style={{ padding: 40 }}>
          <SkeletonRows rows={3} label="Loading your computers" />
        </Panel>
      ) : (
        <>
          <DataTable
            columns={COLUMNS}
            empty={
              machines.length === 0 ? (
                // Names the next action rather than defining the feature, which
                // is what the design system asks an empty state to do.
                <span style={monoPlain}>
                  Turn on sync in Mauvely Suite to add this computer.
                </span>
              ) : undefined
            }
          >
            {rows(machines, false)}
          </DataTable>

          {apps.length > 0 && (
            <div style={{ display: 'grid', gap: 10 }}>
              <Eyebrow>Apps</Eyebrow>
              <DataTable columns={APP_COLUMNS}>{rows(apps, true)}</DataTable>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The folder's own name, from the root's `nameEnc`.
 *
 * Wrapped under the master key, like the vault root — which is what lets *this*
 * machine read a folder another machine created, and is the difference between
 * a drive and a backup. See migration 0024.
 *
 * A name that will not decrypt is reported per row rather than failing the
 * page: one unreadable computer should not hide the others.
 */
async function withFolderName(c: ComputerWire): Promise<Computer> {
  if (!c.rootNodeId || !c.nodeKeyWrapped || !c.nameEnc) {
    return { ...c, folderName: null };
  }
  try {
    const vault = requireVault();
    const key = await unwrapNodeKey(vault.masterKey, c.nodeKeyWrapped);
    return { ...c, folderName: await decryptName(key, c.nameEnc) };
  } catch {
    return { ...c, folderName: null };
  }
}
